import { apiClient } from '../utils/api-client'
import type { Workout } from '../types/workout'

interface CreateWorkoutPayload {
  scheduled_date?: string | null
  scheduled_week_start?: string
  exercise_id: string
}

export interface ScheduleUpdate {
  id: string
  scheduled_date: string | null
}

interface WorkoutResponse {
  data: { id: string; type: string; attributes: Workout }
}

interface WorkoutsResponse {
  data: Array<{ id: string; type: string; attributes: Workout }>
}

interface ListOptions {
  from?: string
  to?: string
  exerciseId?: string
}

class WorkoutsService {
  async list(options: ListOptions = {}): Promise<Workout[]> {
    const params = new URLSearchParams()
    if (options.from) params.set('from', options.from)
    if (options.to) params.set('to', options.to)
    if (options.exerciseId) params.set('exercise_id', options.exerciseId)
    const query = params.toString() ? `?${params}` : ''
    const response = await apiClient.get(`/api/workouts${query}`, { requireAuth: true })
    const json: WorkoutsResponse = await response.json()
    return json.data.map((d) => ({ ...d.attributes, id: d.id }))
  }

  async show(id: string): Promise<Workout> {
    const response = await apiClient.get(`/api/workouts/${id}`, { requireAuth: true })
    const json: WorkoutResponse = await response.json()
    return { ...json.data.attributes, id: json.data.id }
  }

  async create(payload: CreateWorkoutPayload): Promise<Workout> {
    const response = await apiClient.post('/api/workouts', {
      data: { attributes: payload },
    }, { requireAuth: true })
    const json: WorkoutResponse = await response.json()
    return { ...json.data.attributes, id: json.data.id }
  }

  async destroy(id: string): Promise<void> {
    await apiClient.delete(`/api/workouts/${id}`, { requireAuth: true })
  }

  /**
   * Renumbers one day's workouts. `ids` must be that day's whole list in the
   * order it should be done in — the server refuses a partial one.
   */
  async reorder(ids: string[]): Promise<Workout[]> {
    const response = await apiClient.patch('/api/workouts/reorder', {
      data: { attributes: { ids } },
    }, { requireAuth: true })
    const json: WorkoutsResponse = await response.json()
    return json.data.map((d) => ({ ...d.attributes, id: d.id }))
  }

  async bulkSchedule(updates: ScheduleUpdate[]): Promise<Workout[]> {
    const response = await apiClient.patch('/api/workouts/bulk_schedule', {
      data: { attributes: { updates } },
    }, { requireAuth: true })
    const json: WorkoutsResponse = await response.json()
    return json.data.map((d) => ({ ...d.attributes, id: d.id }))
  }
}

export const workoutsService = new WorkoutsService()
